import { useState, useEffect, useMemo, useRef } from "react";
import { DotsVertical, Edit05, Trash01, CheckCircle, AlertCircle, Paperclip, Stars01, Check, X, UserPlus01, XCircle } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { Input } from "@/components/base/input/input";
import { TextArea } from "@/components/base/textarea/textarea";
import { Avatar } from "@/components/base/avatar/avatar";
import type { ReviewPolygon } from "@/types/document-review";
import { cx } from "@/utils/cx";
import { useResolveDocumentSuggestionsWithAI, useResolveSingleSuggestionWithAI, useApplyAICorrection, useMarkSuggestionResolved } from "@/hooks/api/use-suggestion-resolution";
import type { AIResolutionResult } from "@/types/api/suggestion-resolution";
import { useSuggestionChat, useSendChatMessage } from "@/hooks/api/use-suggestion-chat";

// Component for editable attribute row with local state to handle key renaming
function AttributeRow({
    attrKey,
    value,
    onKeyChange,
    onValueChange,
    onRemove,
}: {
    attrKey: string;
    value: string;
    onKeyChange: (oldKey: string, newKey: string) => void;
    onValueChange: (key: string, value: string) => void;
    onRemove: (key: string) => void;
}) {
    const [localKey, setLocalKey] = useState(attrKey);

    // Sync local key when the prop changes (e.g., when polygon changes)
    useEffect(() => {
        setLocalKey(attrKey);
    }, [attrKey]);

    const handleKeyBlur = () => {
        if (localKey !== attrKey && localKey.trim()) {
            onKeyChange(attrKey, localKey.trim());
        }
    };

    return (
        <div className="flex items-center gap-2 mb-2">
            <Input
                label=""
                placeholder="Chiave"
                value={localKey}
                onChange={(newKey) => setLocalKey(newKey as string)}
                onBlur={handleKeyBlur}
                className="flex-1"
            />
            <Input
                label=""
                placeholder="Valore"
                value={value}
                onChange={(newValue) => onValueChange(attrKey, newValue as string)}
                className="flex-1"
            />
            <ButtonUtility
                size="sm"
                color="secondary"
                icon={Trash01}
                onClick={() => onRemove(attrKey)}
                tooltip="Rimuovi attributo"
                className="flex-shrink-0"
            />
        </div>
    );
}

type AnnotationDrawerProps = {
    isOpen: boolean;
    polygon: ReviewPolygon | null;
    allPolygons: ReviewPolygon[];
    documentId: string;
    onClose: () => void;
    onUpdate: (polygonId: string, updates: Partial<ReviewPolygon>) => void;
    onSelectPolygon: (polygonId: string) => void;
    onMarkResolved: (polygonId: string, resolved: boolean) => void;
    onDelete: (polygonId: string) => void;
    onCreatePolygon?: () => void;
    // Document actions
    onValidate?: () => void;
    onInvalidate?: () => void;
    onShare?: () => void;
    isValidating?: boolean;
    isValidated?: boolean;
};

export function AnnotationDrawer({
    polygon,
    allPolygons,
    documentId,
    onUpdate,
    onSelectPolygon,
    onMarkResolved,
    onDelete,
    onCreatePolygon,
    onValidate,
    onInvalidate,
    onShare,
    isValidating = false,
    isValidated = false,
}: AnnotationDrawerProps) {
    const [activeTab, setActiveTab] = useState<'details' | 'chat' | 'ai' | 'all'>('all');
    const [name, setName] = useState("");
    const [notes, setNotes] = useState("");
    const [attributes, setAttributes] = useState<Record<string, string>>({});
    const [chatMessage, setChatMessage] = useState("");
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [resolveModalOpen, setResolveModalOpen] = useState(false);
    const [actionPolygonId, setActionPolygonId] = useState<string | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'resolved'>('all');
    const [searchQuery, setSearchQuery] = useState("");
    const chatListRef = useRef<HTMLDivElement | null>(null);

    // AI Resolution state
    const [aiResolutions, setAiResolutions] = useState<Record<string, AIResolutionResult>>({});
    const [applyingCorrection, setApplyingCorrection] = useState<string | null>(null);

    // AI Resolution hooks
    const resolveWithAI = useResolveDocumentSuggestionsWithAI();
    const resolveSingleWithAI = useResolveSingleSuggestionWithAI();
    const applyCorrection = useApplyAICorrection();
    useMarkSuggestionResolved(); // Hook may have side effects

    // Chat hooks - fetch messages for selected polygon
    const { data: chatMessages = [], isLoading: isChatLoading } = useSuggestionChat(polygon?.id);
    const sendChatMutation = useSendChatMessage();

    // Reset form fields when a DIFFERENT polygon is selected (by ID)
    // Using polygon.id as dependency prevents resetting when the same polygon's
    // data updates (e.g., from API refetch), which would clear user's unsaved edits
    useEffect(() => {
        if (polygon) {
            setName(polygon.name || "");
            setNotes(polygon.notes || "");
            setAttributes(polygon.attributes || {});
        } else {
            setName("");
            setNotes("");
            setAttributes({});
        }
    }, [polygon?.id]);

    const filteredPolygons = useMemo(() => {
        return allPolygons.filter((p) => {
            // Filtro per stato
            const isResolved = p.attributes?.resolved === 'true';
            if (statusFilter === 'pending' && isResolved) return false;
            if (statusFilter === 'resolved' && !isResolved) return false;

            // Filtro per ricerca
            if (searchQuery.trim()) {
                const query = searchQuery.toLowerCase();
                const searchText = [
                    p.name?.toLowerCase(),
                    p.notes?.toLowerCase(),
                    `pagina ${p.pageNumber}`,
                ].filter(Boolean).join(' ');

                if (!searchText.includes(query)) return false;
            }

            return true;
        });
    }, [allPolygons, statusFilter, searchQuery]);

    const handleSave = () => {
        console.log('[AnnotationDrawer] handleSave called');
        console.log('[AnnotationDrawer] polygon:', polygon);
        console.log('[AnnotationDrawer] name:', name, 'notes:', notes, 'attributes:', attributes);
        if (!polygon) {
            console.warn('[AnnotationDrawer] No polygon selected, returning');
            return;
        }
        console.log('[AnnotationDrawer] Calling onUpdate with polygonId:', polygon.id);
        onUpdate(polygon.id, { name, notes, attributes });
        console.log('[AnnotationDrawer] onUpdate called successfully');
    };

    const handleAddAttribute = () => {
        setAttributes((prev) => ({ ...prev, [`new_attribute_${Date.now()}`]: "" }));
    };

    const handleAttributeChange = (key: string, value: string) => {
        setAttributes((prev) => ({ ...prev, [key]: value }));
    };

    const handleRemoveAttribute = (keyToRemove: string) => {
        setAttributes((prev) => {
            const newAttributes = { ...prev };
            delete newAttributes[keyToRemove];
            return newAttributes;
        });
    };

    const handleAddComment = () => {
        if (!polygon || !chatMessage.trim()) return;

        sendChatMutation.mutate(
            { suggestionId: polygon.id, message: chatMessage },
            {
                onSuccess: () => {
                    setChatMessage("");
                    // Auto-scroll to bottom after adding comment
                    setTimeout(() => {
                        chatListRef.current?.scrollTo({ top: chatListRef.current.scrollHeight, behavior: "smooth" });
                    }, 100);
                },
                onError: (error) => {
                    console.error('[AnnotationDrawer] Failed to send chat message:', error);
                },
            }
        );
    };

    // Auto-scroll chat when messages change
    useEffect(() => {
        if (activeTab === 'chat' && chatMessages.length > 0) {
            chatListRef.current?.scrollTo({ top: chatListRef.current.scrollHeight, behavior: "smooth" });
        }
    }, [chatMessages.length, activeTab]);

    // AI Resolution handlers
    const handleResolveWithAI = async () => {
        if (!documentId) return;

        try {
            const response = await resolveWithAI.mutateAsync(documentId);
            // Store AI resolutions by suggestion ID
            const newResolutions: Record<string, AIResolutionResult> = {};
            response.results.forEach((result) => {
                newResolutions[result.suggestion_id] = result;
            });
            setAiResolutions(newResolutions);
        } catch (error) {
            console.error('AI resolution failed:', error);
        }
    };

    const handleApplyAICorrection = async (suggestionId: string, correctedValue: string) => {
        setApplyingCorrection(suggestionId);
        try {
            await applyCorrection.mutateAsync({
                suggestionId,
                correctedValue,
            });
            // Remove from AI resolutions after applying
            setAiResolutions((prev) => {
                const newResolutions = { ...prev };
                delete newResolutions[suggestionId];
                return newResolutions;
            });
            // Refresh the polygon list
            onUpdate(suggestionId, {});
        } catch (error) {
            console.error('Failed to apply correction:', error);
        } finally {
            setApplyingCorrection(null);
        }
    };

    const handleRejectAICorrection = (suggestionId: string) => {
        setAiResolutions((prev) => {
            const newResolutions = { ...prev };
            delete newResolutions[suggestionId];
            return newResolutions;
        });
    };

    // Handler for single suggestion AI resolution
    const handleResolveSingleWithAI = async (suggestionId: string) => {
        try {
            const result = await resolveSingleWithAI.mutateAsync(suggestionId);
            // Store the result
            setAiResolutions((prev) => ({
                ...prev,
                [suggestionId]: result,
            }));
            // Switch to AI tab to show the result
            setActiveTab('ai');
        } catch (error) {
            console.error('Single AI resolution failed:', error);
        }
    };

    const pendingCount = allPolygons.filter((p) => p.attributes?.resolved !== 'true').length;

    return (
        <div className="h-full w-full lg:w-[380px] bg-primary border-l border-secondary flex flex-col overflow-hidden">
            <div className="p-4 border-b border-secondary">
                {/* Action buttons row */}
                {(onValidate || onInvalidate || onShare) && (
                    <div className="flex items-center gap-2 mb-3">
                        {isValidated ? (
                            onInvalidate && (
                                <Button
                                    color="primary-destructive"
                                    size="sm"
                                    iconLeading={XCircle}
                                    onClick={onInvalidate}
                                    isLoading={isValidating}
                                    className="flex-1"
                                >
                                    Invalidate
                                </Button>
                            )
                        ) : (
                            onValidate && (
                                <Button
                                    color="secondary"
                                    size="sm"
                                    iconLeading={CheckCircle}
                                    onClick={onValidate}
                                    isLoading={isValidating}
                                    className="flex-1"
                                >
                                    Validate
                                </Button>
                            )
                        )}
                        {onShare && (
                            <Button
                                color="primary"
                                size="sm"
                                iconLeading={UserPlus01}
                                onClick={onShare}
                                className="flex-1"
                            >
                                Share
                            </Button>
                        )}
                    </div>
                )}

                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <div className="text-md font-semibold text-primary">Annotazioni</div>
                        {onCreatePolygon && (
                            <Button
                                size="sm"
                                color="primary"
                                onClick={onCreatePolygon}
                                className="text-xs"
                            >
                                + Aggiungi
                            </Button>
                        )}
                    </div>
                    {pendingCount > 0 && (
                        <span className="text-xs text-warning-600">{pendingCount} da risolvere</span>
                    )}
                </div>

                {/* TABS */}
                <div className="flex gap-1 border-b border-secondary overflow-x-auto">
                    <button
                        onClick={() => setActiveTab('all')}
                        className={`px-3 py-2 text-sm whitespace-nowrap ${activeTab === 'all' ? 'border-b-2 border-brand-600 text-brand-600' : 'text-tertiary'}`}
                    >
                        Tutti ({allPolygons.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('details')}
                        className={`px-3 py-2 text-sm whitespace-nowrap ${activeTab === 'details' ? 'border-b-2 border-brand-600 text-brand-600' : 'text-tertiary'}`}
                    >
                        Dettagli
                    </button>
                    <button
                        onClick={() => setActiveTab('chat')}
                        className={`px-3 py-2 text-sm whitespace-nowrap ${activeTab === 'chat' ? 'border-b-2 border-brand-600 text-brand-600' : 'text-tertiary'}`}
                    >
                        Chat
                    </button>
                    <button
                        onClick={() => setActiveTab('ai')}
                        className={`px-3 py-2 text-sm whitespace-nowrap flex items-center gap-1 ${activeTab === 'ai' ? 'border-b-2 border-brand-600 text-brand-600' : 'text-tertiary'}`}
                    >
                        <Stars01 className="size-3" />
                        AI Solutions
                        {Object.keys(aiResolutions).length > 0 && (
                            <span className="ml-1 px-1.5 py-0.5 bg-brand-100 text-brand-700 text-[10px] rounded-full">
                                {Object.keys(aiResolutions).length}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            {/* Stop mouse events from propagating to prevent Fabric.js canvas from stealing focus */}
            <div className="flex-1 overflow-auto p-4" onMouseDown={(e) => e.stopPropagation()}>
                {/* TAB: Details */}
                {activeTab === 'details' && polygon && (
                    <div className="space-y-6">
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-primary mb-1">
                                Nome Segnalazione
                            </label>
                            <Input
                                id="name"
                                placeholder="Es: Errore su importo"
                                value={name}
                                onChange={(value) => setName(value as string)}
                            />
                        </div>
                        <div>
                            <label htmlFor="notes" className="block text-sm font-medium text-primary mb-1">
                                Note
                            </label>
                            <textarea
                                id="notes"
                                className="w-full rounded-lg border border-secondary bg-primary px-3 py-2 text-sm text-primary shadow-xs-skeumorphic focus:border-brand-solid focus:ring-brand-solid"
                                rows={4}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                            />
                        </div>

                        <div>
                            <h3 className="text-md font-medium text-primary mb-2">Attributi</h3>
                            {Object.entries(attributes).map(([key, value]) => (
                                <AttributeRow
                                    key={key}
                                    attrKey={key}
                                    value={value}
                                    onKeyChange={(oldKey, newKey) => {
                                        setAttributes((prev) => {
                                            const newAttrs: Record<string, string> = {};
                                            // Preserve order by iterating through entries
                                            for (const [k, v] of Object.entries(prev)) {
                                                if (k === oldKey) {
                                                    newAttrs[newKey] = v;
                                                } else {
                                                    newAttrs[k] = v;
                                                }
                                            }
                                            return newAttrs;
                                        });
                                    }}
                                    onValueChange={handleAttributeChange}
                                    onRemove={handleRemoveAttribute}
                                />
                            ))}
                            <Button size="sm" color="secondary" onClick={handleAddAttribute} className="mt-2">
                                Aggiungi Attributo
                            </Button>
                        </div>

                        <div className="pt-4 border-t border-secondary">
                            <Button size="md" color="primary" onClick={handleSave} className="w-full">
                                Salva Modifiche
                            </Button>
                        </div>
                    </div>
                )}

                {/* TAB: Chat */}
                {activeTab === 'chat' && polygon && (
                    <div className="flex h-full min-h-0 flex-col">
                        <div ref={chatListRef} className="flex-1 overflow-y-auto py-4">
                            <div className="flex flex-col gap-5">
                                {isChatLoading ? (
                                    <div className="text-center text-tertiary py-8">
                                        <div className="text-sm">Caricamento messaggi...</div>
                                    </div>
                                ) : chatMessages.length > 0 ? (
                                    chatMessages.map((msg) => {
                                        const formatTimestamp = (ts: string) => {
                                            try {
                                                return new Date(ts).toLocaleString('it-IT', {
                                                    day: 'numeric',
                                                    month: 'short',
                                                    hour: 'numeric',
                                                    minute: '2-digit',
                                                });
                                            } catch {
                                                return undefined;
                                            }
                                        };
                                        const displayName = msg.external_user_email || 'Utente';

                                        return (
                                            <div key={msg.suggestion_chat_id} className="flex items-start">
                                                <Avatar size="sm" src="https://www.untitledui.com/images/avatars/ethan-valdez?bg=%23E0E0E0" status="online" className="mr-3" />
                                                <div className="flex-1">
                                                    <div className="mb-1 flex items-baseline justify-between pr-1">
                                                        <span className="text-sm font-medium text-primary">{displayName}</span>
                                                        <span className="text-xs text-tertiary">{formatTimestamp(msg.created_at)}</span>
                                                    </div>
                                                    <div className="max-w-[520px] rounded-2xl border border-secondary bg-primary px-4 py-3 text-sm leading-6 text-primary shadow-xs">
                                                        {msg.message}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="text-center text-tertiary py-8">
                                        <div className="text-sm">Nessun commento ancora</div>
                                        <div className="text-xs mt-1">Inizia la conversazione</div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                handleAddComment();
                            }}
                            className="border-t border-secondary py-3"
                        >
                            <div
                                className="relative w-full rounded-lg bg-primary ring-1 ring-primary transition-shadow duration-100 ease-linear focus-within:ring-2 focus-within:ring-brand"
                            >
                                {/* Top-right subtle recording icon */}
                                <div className="pointer-events-none absolute right-3 top-3 flex gap-1 opacity-50">
                                    <Paperclip className="size-4 dark:text-white" />
                                </div>

                                <TextArea
                                    value={chatMessage}
                                    onChange={(val) => setChatMessage(val as string)}
                                    rows={4}
                                    placeholder="Scrivi un messaggio..."
                                    aria-label="Scrivi un messaggio nella chat"
                                    textAreaClassName={(state) =>
                                        cx(
                                            "min-h-[88px] resize-none border-0 bg-transparent px-3.5 pb-10 pt-3 shadow-none ring-0 outline-hidden",
                                            typeof state === "string" && state,
                                        )
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && !e.shiftKey) {
                                            e.preventDefault();
                                            handleAddComment();
                                        }
                                    }}
                                    className=""
                                />

                                {/* Bottom-right actions */}
                                <div className="pointer-events-none absolute bottom-2 right-2 flex items-center gap-2">
                                    <button
                                        type="button"
                                        className="pointer-events-auto rounded-md p-1.5 text-tertiary transition hover:text-tertiary_hover"
                                        aria-label="Allega"
                                    >
                                        <Paperclip className="size-4" />
                                    </button>
                                    <button
                                        type="button"
                                        className="pointer-events-auto rounded-md p-1.5 text-tertiary transition hover:text-tertiary_hover"
                                        aria-label="Emoji"
                                    >
                                        <span className="block text-base leading-none">☺︎</span>
                                    </button>
                                    <Button className="pointer-events-auto" color="link-color" size="md" type="submit">
                                        Invia
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </div>
                )}

                {/* TAB: AI Solutions */}
                {activeTab === 'ai' && (
                    <div className="space-y-4">
                        {/* Header with Resolve All button */}
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-sm font-medium text-primary">Soluzioni AI</h3>
                                <p className="text-xs text-tertiary mt-0.5">
                                    Usa l'AI per trovare correzioni automatiche
                                </p>
                            </div>
                            <Button
                                size="sm"
                                color="primary"
                                iconLeading={Stars01}
                                onClick={handleResolveWithAI}
                                isLoading={resolveWithAI.isPending}
                            >
                                Analizza tutti
                            </Button>
                        </div>

                        {/* AI Results List */}
                        {Object.keys(aiResolutions).length === 0 ? (
                            <div className="p-8 text-center bg-secondary/30 rounded-lg">
                                <Stars01 className="size-8 text-tertiary mx-auto mb-3" />
                                <div className="text-sm text-primary font-medium mb-1">
                                    Nessuna soluzione AI disponibile
                                </div>
                                <div className="text-xs text-tertiary mb-4">
                                    Clicca "Analizza tutti" per generare suggerimenti AI per le segnalazioni pendenti
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {Object.entries(aiResolutions).map(([suggestionId, result]) => {
                                    const polygon = allPolygons.find(p => p.id === suggestionId);
                                    if (!polygon) return null;

                                    return (
                                        <div
                                            key={suggestionId}
                                            className="p-4 rounded-lg border border-brand-200 dark:border-brand-800 bg-brand-50 dark:bg-brand-900/20"
                                        >
                                            {/* Polygon info */}
                                            <div
                                                className="flex items-start justify-between mb-3 cursor-pointer"
                                                onClick={() => onSelectPolygon(suggestionId)}
                                            >
                                                <div>
                                                    <div className="text-sm font-medium text-primary">
                                                        {polygon.name || `Segnalazione #${suggestionId.substring(0, 8)}`}
                                                    </div>
                                                    <div className="text-xs text-tertiary">
                                                        Pagina {polygon.pageNumber}
                                                    </div>
                                                </div>
                                                {result.confidence && (
                                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                        result.confidence >= 0.8
                                                            ? 'bg-success-100 text-success-700'
                                                            : result.confidence >= 0.5
                                                                ? 'bg-warning-100 text-warning-700'
                                                                : 'bg-error-100 text-error-700'
                                                    }`}>
                                                        {Math.round(result.confidence * 100)}% confidenza
                                                    </span>
                                                )}
                                            </div>

                                            {/* AI Suggestion */}
                                            {result.status === 'suggested' && result.ai_suggested_value ? (
                                                <>
                                                    <div className="p-3 bg-white dark:bg-gray-800 rounded-md mb-3">
                                                        <div className="text-xs text-tertiary mb-1">
                                                            Valore suggerito:
                                                        </div>
                                                        <div className="text-sm font-medium text-primary">
                                                            {result.ai_suggested_value}
                                                        </div>
                                                        {result.matched_field_path && (
                                                            <div className="text-xs text-tertiary mt-1">
                                                                Campo: <code className="px-1 py-0.5 bg-secondary rounded">{result.matched_field_path}</code>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Actions */}
                                                    <div className="flex gap-2">
                                                        <Button
                                                            size="sm"
                                                            color="primary"
                                                            iconLeading={Check}
                                                            onClick={() => handleApplyAICorrection(suggestionId, result.ai_suggested_value!)}
                                                            isLoading={applyingCorrection === suggestionId}
                                                            className="flex-1"
                                                        >
                                                            Applica correzione
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            color="secondary"
                                                            iconLeading={X}
                                                            onClick={() => handleRejectAICorrection(suggestionId)}
                                                        >
                                                            Rifiuta
                                                        </Button>
                                                    </div>
                                                </>
                                            ) : result.status === 'no_match' ? (
                                                <div className="flex items-center gap-2 text-sm text-tertiary">
                                                    <AlertCircle className="size-4" />
                                                    Nessuna corrispondenza trovata nei dati estratti
                                                </div>
                                            ) : result.status === 'failed' ? (
                                                <div className="flex items-center gap-2 text-sm text-error-600">
                                                    <AlertCircle className="size-4" />
                                                    Errore: {result.error_message || 'Elaborazione fallita'}
                                                </div>
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Pending issues without AI resolution */}
                        {pendingCount > 0 && Object.keys(aiResolutions).length > 0 && (
                            <div className="pt-4 border-t border-secondary">
                                <div className="text-xs text-tertiary mb-2">
                                    {pendingCount - Object.keys(aiResolutions).length} segnalazioni senza soluzione AI
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* TAB: All Issues */}
                {activeTab === 'all' && (
                    <div className="space-y-3">
                        {/* Filtri e ricerca */}
                        <div className="space-y-2">
                            {/* Filtro stato */}
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setStatusFilter('all')}
                                    className={`px-3 py-1.5 rounded text-sm ${statusFilter === 'all' ? 'bg-brand-solid dark:text-white' : 'bg-secondary text-tertiary hover:bg-secondary_hover'}`}
                                >
                                    Tutti
                                </button>
                                <button
                                    onClick={() => setStatusFilter('pending')}
                                    className={`px-3 py-1.5 rounded text-sm ${statusFilter === 'pending' ? 'bg-brand-solid dark:text-white' : 'bg-secondary text-tertiary hover:bg-secondary_hover'}`}
                                >
                                    Da risolvere
                                </button>
                                <button
                                    onClick={() => setStatusFilter('resolved')}
                                    className={`px-3 py-1.5 rounded text-sm ${statusFilter === 'resolved' ? 'bg-brand-solid dark:text-white' : 'bg-secondary text-tertiary hover:bg-secondary_hover'}`}
                                >
                                    Risolto
                                </button>
                            </div>

                            {/* Campo ricerca */}
                            <Input
                                placeholder="Cerca per nome, note o pagina..."
                                value={searchQuery}
                                onChange={(value) => setSearchQuery(value as string)}
                            />
                        </div>

                        {/* Lista filtrata */}
                        <div className="space-y-2">
                            {allPolygons.length === 0 ? (
                                <div className="p-8 text-center text-tertiary">
                                    <div className="text-sm">Nessun rettangolo creato</div>
                                    <div className="text-xs mt-1">Clicca su "+ Aggiungi Rettangolo" per iniziare</div>
                                </div>
                            ) : filteredPolygons.length === 0 ? (
                                <div className="p-8 text-center text-tertiary">
                                    <div className="text-sm">Nessuna segnalazione trovata</div>
                                    <div className="text-xs mt-1">Prova a modificare i filtri o la ricerca</div>
                                </div>
                            ) : (
                                filteredPolygons.map((p) => {
                                    const aiResult = aiResolutions[p.id];
                                    const hasAISuggestion = aiResult?.status === 'suggested' && aiResult.ai_suggested_value;

                                    return (
                                        <div
                                            key={p.id}
                                            className={`p-3 rounded border cursor-pointer ${p.id === polygon?.id ? 'border-brand-600' : 'border-secondary hover:border-brand-300'} bg-primary`}
                                            onClick={() => {
                                                onSelectPolygon(p.id);
                                                setActiveTab('details');
                                            }}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1">
                                                    <div className="text-sm font-medium text-primary">
                                                        {p.name || `Segnalazione #${p.id.substring(0, 8)}`}
                                                    </div>
                                                    <div className="text-xs text-tertiary mt-1">
                                                        Pagina {p.pageNumber}
                                                        {!p.name && p.notes && ` • ${p.notes}`}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {/* Status icon */}
                                                    {p.attributes?.resolved === 'true' ? (
                                                        <CheckCircle className="size-5 text-success-600" />
                                                    ) : (
                                                        <AlertCircle className="size-5 text-warning-600" />
                                                    )}

                                                    {/* Dropdown menu */}
                                                    <div className="relative">
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setDropdownOpen(dropdownOpen === p.id ? null : p.id);
                                                            }}
                                                            className="p-1 hover:bg-secondary rounded"
                                                        >
                                                            <DotsVertical className="size-4 text-tertiary" />
                                                        </button>

                                                        {dropdownOpen === p.id && (
                                                            <div className="absolute right-0 mt-1 w-48 bg-primary border border-secondary rounded-lg shadow-lg z-10">
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        onSelectPolygon(p.id);
                                                                        setActiveTab('details');
                                                                        setDropdownOpen(null);
                                                                    }}
                                                                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-primary hover:bg-secondary"
                                                                >
                                                                    <Edit05 className="size-4" />
                                                                    Modifica
                                                                </button>
                                                                {/* Resolve with AI button - only show for pending issues */}
                                                                {p.attributes?.resolved !== 'true' && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setDropdownOpen(null);
                                                                            handleResolveSingleWithAI(p.id);
                                                                        }}
                                                                        disabled={resolveSingleWithAI.isPending}
                                                                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-brand-600 hover:bg-secondary disabled:opacity-50"
                                                                    >
                                                                        <Stars01 className="size-4" />
                                                                        {resolveSingleWithAI.isPending ? 'Analizzando...' : 'Risolvi con AI'}
                                                                    </button>
                                                                )}
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setActionPolygonId(p.id);
                                                                        setResolveModalOpen(true);
                                                                        setDropdownOpen(null);
                                                                    }}
                                                                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-primary hover:bg-secondary"
                                                                >
                                                                    <CheckCircle className="size-4" />
                                                                    {p.attributes?.resolved === 'true' ? 'Segna come da risolvere' : 'Segna come risolto'}
                                                                </button>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setActionPolygonId(p.id);
                                                                        setDeleteModalOpen(true);
                                                                        setDropdownOpen(null);
                                                                    }}
                                                                    className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-secondary"
                                                                >
                                                                    <Trash01 className="size-4" />
                                                                    Elimina
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* AI Suggestion Box */}
                                            {hasAISuggestion && (
                                                <div
                                                    className="mt-3 p-3 bg-brand-50 dark:bg-brand-900/20 rounded-lg border border-brand-200 dark:border-brand-800"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Stars01 className="size-4 text-brand-600" />
                                                        <span className="text-xs font-medium text-brand-700 dark:text-brand-300">
                                                            Suggerimento AI ({Math.round((aiResult.confidence || 0) * 100)}% confidenza)
                                                        </span>
                                                    </div>
                                                    <div className="text-sm text-primary mb-2 font-medium">
                                                        {aiResult.ai_suggested_value}
                                                    </div>
                                                    {aiResult.matched_field_path && (
                                                        <div className="text-xs text-tertiary mb-2">
                                                            Campo: {aiResult.matched_field_path}
                                                        </div>
                                                    )}
                                                    <div className="flex gap-2">
                                                        <Button
                                                            size="sm"
                                                            color="primary"
                                                            onClick={() => handleApplyAICorrection(p.id, aiResult.ai_suggested_value!)}
                                                            disabled={applyingCorrection === p.id}
                                                            className="flex items-center gap-1"
                                                        >
                                                            <Check className="size-3" />
                                                            {applyingCorrection === p.id ? 'Applicando...' : 'Applica'}
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            color="secondary"
                                                            onClick={() => handleRejectAICorrection(p.id)}
                                                            className="flex items-center gap-1"
                                                        >
                                                            <X className="size-3" />
                                                            Rifiuta
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* AI No Match / Failed status */}
                                            {aiResult && aiResult.status !== 'suggested' && (
                                                <div className="mt-2 text-xs text-tertiary">
                                                    {aiResult.status === 'no_match' && (
                                                        <span className="flex items-center gap-1">
                                                            <AlertCircle className="size-3" />
                                                            AI non ha trovato una corrispondenza
                                                        </span>
                                                    )}
                                                    {aiResult.status === 'failed' && (
                                                        <span className="flex items-center gap-1 text-error-600">
                                                            <AlertCircle className="size-3" />
                                                            Errore: {aiResult.error_message || 'Elaborazione fallita'}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}

                {!polygon && activeTab !== 'all' && (
                    <div className="p-4 text-tertiary">Seleziona un rettangolo per visualizzare le annotazioni.</div>
                )}
            </div>

            {/* Modale Delete */}
            {deleteModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
                    <div className="bg-primary rounded-lg p-6 max-w-md">
                        <h3 className="text-lg font-semibold text-primary mb-2">Elimina Segnalazione</h3>
                        <p className="text-sm text-tertiary mb-4">
                            Sei sicuro di voler eliminare questa segnalazione? Questa azione non può essere annullata.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <Button color="secondary" onClick={() => setDeleteModalOpen(false)}>
                                Annulla
                            </Button>
                            <Button
                                color="primary"
                                onClick={() => {
                                    if (actionPolygonId) {
                                        onDelete(actionPolygonId);
                                    }
                                    setDeleteModalOpen(false);
                                    setActionPolygonId(null);
                                }}
                            >
                                Elimina
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modale Resolve */}
            {resolveModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
                    <div className="bg-primary rounded-lg p-6 max-w-md">
                        <h3 className="text-lg font-semibold text-primary mb-2">Cambia Stato</h3>
                        <p className="text-sm text-tertiary mb-4">
                            Confermi di voler cambiare lo stato di questa segnalazione?
                        </p>
                        <div className="flex gap-3 justify-end">
                            <Button color="secondary" onClick={() => setResolveModalOpen(false)}>
                                Annulla
                            </Button>
                            <Button
                                color="primary"
                                onClick={() => {
                                    if (actionPolygonId) {
                                        const p = allPolygons.find(poly => poly.id === actionPolygonId);
                                        if (p) {
                                            onMarkResolved(actionPolygonId, !(p.attributes?.resolved === 'true'));
                                        }
                                    }
                                    setResolveModalOpen(false);
                                    setActionPolygonId(null);
                                }}
                            >
                                Conferma
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}